import { AxiosError, AxiosResponse } from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
	FileType,
	StringRecognizedLabel,
	TableCellCandidateType,
	TableColumnDraft,
} from 'entities/Files/types';
import { getModelKey } from 'utils/getModelKey';
import { getUserPreferences, updateUserPreferences } from 'entities/Preferences/api';
import { ColumnPreference, UserPreferences } from 'entities/Preferences/types';
import { showErrorNotification } from 'shared/helpers/showErrorNotification';
import { useFileEditorStore } from 'store/file-editor';
import { FileViewerHandle } from 'components/file-editor-components/file-viewer';
import {
	getCellSourceId,
	getLabelSourceId,
	regionsToBoundingRegions,
	Region,
} from 'components/file-editor-components/file-viewer/region-types';
import { CellTarget } from 'components/file-editor-components/table-fields';
import {
	computeRegionsFromFileDetails,
	useFileEditorRegions,
} from 'components/file-editor-components/useFileEditorRegions';
import { getItemCode } from 'shared/helpers/getItemCode';

const PREFERENCES_QUERY_KEY = ['get/users/preferences'];

type RegionTextParams = {
	id: string | undefined;
	data: {
		pageNumber: number;
		boxPosition: { x: number; y: number; width: number; height: number };
	};
};

type UseFileEditorParams = {
	entityId: string | undefined;
	detailsQueryKey: unknown[];
	urlQueryKey: unknown[];
	regionTextQueryKey?: string;
	getDetails: (id: string | undefined) => Promise<AxiosResponse<FileType>>;
	getUrl: (id: string) => Promise<AxiosResponse<{ url: string; expiresAt: number }>>;
	getRegionText?: (params: RegionTextParams) => Promise<AxiosResponse>;
};

export const useFileEditor = ({
	entityId,
	detailsQueryKey,
	urlQueryKey,
	regionTextQueryKey,
	getDetails,
	getUrl,
	getRegionText,
}: UseFileEditorParams) => {
	const {
		fileDetails,
		setFileDetailsV2,
		updateRecognizedLabels,
		updateTableCell,
		updateLabelContent,
		updateLabel,
		addTableRow,
		addTable,
		reset,
	} = useFileEditorStore();

	const fileViewerRef = useRef<FileViewerHandle>(null);
	const [pendingRegion, setPendingRegion] = useState<Region | null>(null);
	const [drawMode, setDrawMode] = useState(false);
	const [pendingCellTarget, setPendingCellTarget] = useState<CellTarget | null>(null);
	const [pendingLabelTarget, setPendingLabelTarget] = useState<number | null>(null);
	const [pendingRegionLink, setPendingRegionLink] = useState<
		| { kind: 'cell'; target: CellTarget; region: Region }
		| { kind: 'label'; labelIndex: number; region: Region }
		| null
	>(null);

	const { isLoading: isDetailsLoading } = useQuery({
		queryKey: detailsQueryKey,
		queryFn: () => getDetails(entityId),
		refetchOnWindowFocus: false,
		onSuccess: (response) => {
			setFileDetailsV2(response.data);
		},
		onError: (err) => {
			showErrorNotification(err as AxiosError);
		},
	});

	const { data: fileUrlResponse, isLoading: isUrlLoading } = useQuery({
		queryKey: urlQueryKey,
		queryFn: () => getUrl(entityId as string),
		refetchOnWindowFocus: false,
		refetchInterval: (data) => {
			if (!data?.data?.expiresAt) return false;
			const msUntilExpiry = data.data.expiresAt - Date.now();
			return msUntilExpiry > 0 ? msUntilExpiry : 0;
		},
		onError: (err) => {
			showErrorNotification(err as AxiosError);
		},
	});

	const fileUrl = fileUrlResponse?.data?.url;

	const queryClient = useQueryClient();
	const { data: preferencesResponse } = useQuery({
		queryKey: PREFERENCES_QUERY_KEY,
		queryFn: getUserPreferences,
		refetchOnWindowFocus: false,
	});
	const { mutate: mutatePreferences } = useMutation({
		mutationKey: ['post/users/preferences'],
		mutationFn: updateUserPreferences,
		onError: (err) => {
			showErrorNotification(err as AxiosError);
			queryClient.invalidateQueries({ queryKey: PREFERENCES_QUERY_KEY });
		},
	});

	const modelKey = fileDetails && getModelKey(fileDetails);

	const getColumnPinPreferences = useCallback(
		(tableLabel: string | undefined): ColumnPreference[] => {
			if (!modelKey || !tableLabel) return [];
			return preferencesResponse?.data.modelPreferences?.[modelKey]?.tables?.[tableLabel] ?? [];
		},
		[modelKey, preferencesResponse],
	);

	const handleTogglePinColumn = useCallback(
		(tableIndex: number, columnIndex: number) => {
			const table = fileDetails?.recognizedLabels[tableIndex];
			if (!fileDetails || !modelKey || !table || table.type !== 'table' || !table.label) return;

			const tableLabel = table.label;
			const currentPrefs = getColumnPinPreferences(tableLabel);
			const pinnedByName = new Map(currentPrefs.map((pref) => [pref.columnName, pref.isPinned]));
			const nextPrefs: ColumnPreference[] = table.columnsHeaders.map((header, index) => ({
				columnName: header.columnName,
				isPinned:
					index === columnIndex
						? !(pinnedByName.get(header.columnName) ?? false)
						: pinnedByName.get(header.columnName) ?? false,
			}));

			if (preferencesResponse) {
				queryClient.setQueryData<AxiosResponse<UserPreferences>>(PREFERENCES_QUERY_KEY, {
					...preferencesResponse,
					data: {
						...preferencesResponse.data,
						modelPreferences: {
							...preferencesResponse.data.modelPreferences,
							[modelKey]: {
								...preferencesResponse.data.modelPreferences?.[modelKey],
								tables: {
									...preferencesResponse.data.modelPreferences?.[modelKey]?.tables,
									[tableLabel]: nextPrefs,
								},
							},
						},
					},
				});
			}
			mutatePreferences({ modelPreferences: { [modelKey]: { tables: { [tableLabel]: nextPrefs } } } });
		},
		[fileDetails, modelKey, getColumnPinPreferences, preferencesResponse, queryClient, mutatePreferences],
	);

	const { regions, setRegions, selectedSourceId, setSelectedSourceId } = useFileEditorRegions(fileDetails);

	useEffect(() => () => reset(), []);

	useQuery({
		queryKey: [
			regionTextQueryKey,
			pendingRegionLink?.region.pageIndex,
			pendingRegionLink?.region.x,
			pendingRegionLink?.region.y,
			pendingRegionLink?.region.width,
			pendingRegionLink?.region.height,
		],
		queryFn: () =>
			getRegionText &&
			getRegionText({
				id: entityId,
				data: {
					pageNumber: (pendingRegionLink?.region.pageIndex ?? 0) + 1,
					boxPosition: {
						x: pendingRegionLink?.region.x ?? 0,
						y: pendingRegionLink?.region.y ?? 0,
						width: pendingRegionLink?.region.width ?? 0,
						height: pendingRegionLink?.region.height ?? 0,
					},
				},
			}),
		onSuccess: (response) => {
			if (!pendingRegionLink) return;
			const content = (response?.data as string) || '';
			if (pendingRegionLink.kind === 'cell') {
				const { tableIndex, rowIndex, columnIndex } = pendingRegionLink.target;
				updateTableCell(tableIndex, rowIndex, columnIndex, { content });
			} else {
				updateLabel(pendingRegionLink.labelIndex, { content });
			}
			setPendingRegionLink(null);
		},
		onError: (err) => {
			showErrorNotification(err as AxiosError);
			setPendingRegionLink(null);
		},
		enabled: !!pendingRegionLink && !!entityId,
	});

	const handleCancelLabel = () => {
		setPendingRegion(null);
	};

	const handleSaveLabel = ({ name, text }: { name: string; text: string }) => {
		if (!pendingRegion || !fileDetails) return;

		const labelIndex = (fileDetails.recognizedLabels || []).length;
		const sourceId = getLabelSourceId(labelIndex);
		const newLabel: StringRecognizedLabel = {
			type: 'string',
			label: name,
			content: text,
			boundingRegions: regionsToBoundingRegions([pendingRegion]),
		};

		fileViewerRef.current?.confirmPendingRegion(pendingRegion, sourceId);
		updateRecognizedLabels([...(fileDetails.recognizedLabels || []), newLabel]);
		setPendingRegion(null);
	};

	const handleCellTargetClick = useCallback((target: CellTarget) => {
		setPendingCellTarget(target);
	}, []);

	const handleLabelTargetClick = useCallback((labelIndex: number) => {
		setPendingLabelTarget(labelIndex);
	}, []);

	const handleCellContentChange = useCallback(
		(target: CellTarget, content: string) => {
			updateTableCell(target.tableIndex, target.rowIndex, target.columnIndex, { content });
		},
		[updateTableCell],
	);

	const handleCellCandidateSelect = useCallback(
		(target: CellTarget, candidate: TableCellCandidateType) => {
			updateTableCell(target.tableIndex, target.rowIndex, target.columnIndex, {
				content: getItemCode(candidate),
				manualOverride: candidate,
			});
		},
		[updateTableCell],
	);

	const handleDeleteEntry = useCallback(
		(entryIndex: number) => {
			if (!fileDetails) return;
			const recognizedLabels = fileDetails.recognizedLabels.filter((_, index) => index !== entryIndex);
			updateRecognizedLabels(recognizedLabels);
			fileViewerRef.current?.resyncRegions(
				computeRegionsFromFileDetails({ ...fileDetails, recognizedLabels }),
			);
		},
		[fileDetails, updateRecognizedLabels],
	);

	const handleAddRow = useCallback(
		(tableIndex: number) => {
			addTableRow(tableIndex);
		},
		[addTableRow],
	);

	const handleAddTable = useCallback(
		(tableName: string, columns: TableColumnDraft[]) => {
			addTable(
				tableName,
				columns.map((column) => column.columnName),
			);
		},
		[addTable],
	);

	const handleUpdateTable = useCallback(
		(tableIndex: number, tableName: string, columns: TableColumnDraft[]) => {
			if (!fileDetails) return;
			const table = fileDetails.recognizedLabels[tableIndex];
			if (!table || table.type !== 'table') return;

			const oldToNewIndex = new Map(
				columns.flatMap((column, newIndex) =>
					column.sourceIndex === null ? [] : [[column.sourceIndex, newIndex] as const],
				),
			);
			const recognizedLabels = fileDetails.recognizedLabels.map((entry, index) => {
				if (index !== tableIndex || entry.type !== 'table') return entry;
				return {
					...entry,
					label: tableName,
					columnsHeaders: columns.map((column) =>
						column.sourceIndex === null
							? { columnName: column.columnName }
							: { ...entry.columnsHeaders[column.sourceIndex], columnName: column.columnName },
					),
					cells: entry.cells
						.filter((cell) => oldToNewIndex.has(cell.columnIndex))
						.map((cell) => ({
							...cell,
							columnIndex: oldToNewIndex.get(cell.columnIndex) as number,
						})),
				};
			});
			updateRecognizedLabels(recognizedLabels);
			fileViewerRef.current?.resyncRegions(
				computeRegionsFromFileDetails({ ...fileDetails, recognizedLabels }),
			);
		},
		[fileDetails, updateRecognizedLabels],
	);

	const handleCellDelete = useCallback(
		(target: CellTarget) => {
			fileViewerRef.current?.deleteRegionBySourceId(
				getCellSourceId(target.tableIndex, target.rowIndex, target.columnIndex),
			);
			if (!fileDetails) return;
			const table = fileDetails.recognizedLabels[target.tableIndex];
			if (!table || table.type !== 'table') return;

			const recognizedLabels = fileDetails.recognizedLabels.map((entry, index) =>
				index === target.tableIndex && entry.type === 'table'
					? {
							...entry,
							cells: entry.cells.filter(
								(cell) =>
									!(
										cell.rowIndex === target.rowIndex &&
										cell.columnIndex === target.columnIndex
									),
							),
					  }
					: entry,
			);
			updateRecognizedLabels(recognizedLabels);
		},
		[fileDetails, updateRecognizedLabels],
	);

	const handleDeleteRow = useCallback(
		(tableIndex: number, rowIndex: number) => {
			if (!fileDetails) return;
			const table = fileDetails.recognizedLabels[tableIndex];
			if (!table || table.type !== 'table') return;

			table.cells
				.filter((cell) => cell.rowIndex === rowIndex)
				.forEach((cell) => {
					fileViewerRef.current?.deleteRegionBySourceId(
						getCellSourceId(tableIndex, rowIndex, cell.columnIndex),
					);
				});

			const recognizedLabels = fileDetails.recognizedLabels.map((entry, index) =>
				index === tableIndex && entry.type === 'table'
					? { ...entry, cells: entry.cells.filter((cell) => cell.rowIndex !== rowIndex) }
					: entry,
			);
			updateRecognizedLabels(recognizedLabels);
		},
		[fileDetails, updateRecognizedLabels],
	);

	const handleDeleteColumn = useCallback(
		(tableIndex: number, columnIndex: number) => {
			if (!fileDetails) return;
			const table = fileDetails.recognizedLabels[tableIndex];
			if (!table || table.type !== 'table') return;

			const recognizedLabels = fileDetails.recognizedLabels.map((entry, index) => {
				if (index !== tableIndex || entry.type !== 'table') return entry;
				return {
					...entry,
					columnsHeaders: entry.columnsHeaders.filter((_, i) => i !== columnIndex),
					cells: entry.cells
						.filter((cell) => cell.columnIndex !== columnIndex)
						.map((cell) =>
							cell.columnIndex > columnIndex
								? { ...cell, columnIndex: cell.columnIndex - 1 }
								: cell,
						),
				};
			});
			updateRecognizedLabels(recognizedLabels);
			fileViewerRef.current?.resyncRegions(
				computeRegionsFromFileDetails({ ...fileDetails, recognizedLabels }),
			);
		},
		[fileDetails, updateRecognizedLabels],
	);

	const handleReorderColumns = useCallback(
		(tableIndex: number, columnOrder: number[]) => {
			if (!fileDetails) return;
			const table = fileDetails.recognizedLabels[tableIndex];
			if (!table || table.type !== 'table') return;

			const oldToNewIndex = new Map(columnOrder.map((oldIndex, newIndex) => [oldIndex, newIndex]));
			const recognizedLabels = fileDetails.recognizedLabels.map((entry, index) => {
				if (index !== tableIndex || entry.type !== 'table') return entry;
				return {
					...entry,
					columnsHeaders: columnOrder.map((oldIndex) => entry.columnsHeaders[oldIndex]),
					cells: entry.cells.map((cell) => ({
						...cell,
						columnIndex: oldToNewIndex.get(cell.columnIndex) ?? cell.columnIndex,
					})),
				};
			});
			updateRecognizedLabels(recognizedLabels);
			fileViewerRef.current?.resyncRegions(
				computeRegionsFromFileDetails({ ...fileDetails, recognizedLabels }),
			);
		},
		[fileDetails, updateRecognizedLabels],
	);

	const handleRegionCreated = (region: Region) => {
		if (pendingCellTarget) {
			const { tableIndex, rowIndex, columnIndex } = pendingCellTarget;
			const sourceId = getCellSourceId(tableIndex, rowIndex, columnIndex);
			const table = fileDetails?.recognizedLabels[tableIndex];
			const existingCell =
				table?.type === 'table'
					? table.cells.find(
							(cell) => cell.rowIndex === rowIndex && cell.columnIndex === columnIndex,
					  )
					: undefined;
			const hadContent = Boolean(existingCell?.content);

			fileViewerRef.current?.confirmPendingRegion(region, sourceId);
			updateTableCell(tableIndex, rowIndex, columnIndex, {
				boundingRegions: regionsToBoundingRegions([region]),
			});
			if (!hadContent) setPendingRegionLink({ kind: 'cell', target: pendingCellTarget, region });
			setPendingCellTarget(null);
			return;
		}

		if (pendingLabelTarget !== null) {
			const sourceId = getLabelSourceId(pendingLabelTarget);
			const existingLabel = fileDetails?.recognizedLabels[pendingLabelTarget];
			const hadContent = existingLabel?.type === 'string' ? Boolean(existingLabel.content) : false;

			fileViewerRef.current?.confirmPendingRegion(region, sourceId);
			updateLabel(pendingLabelTarget, { boundingRegions: regionsToBoundingRegions([region]) });
			if (!hadContent) {
				setPendingRegionLink({ kind: 'label', labelIndex: pendingLabelTarget, region });
			}
			setPendingLabelTarget(null);
		}
	};

	const handleRegionUpdated = useCallback(
		(region: Region) => {
			if (!fileDetails) return;
			const boundingRegions = regionsToBoundingRegions([region]);

			fileDetails.recognizedLabels.forEach((label, labelIndex) => {
				if (label.type === 'table') {
					label.cells.forEach((cell) => {
						if (
							getCellSourceId(labelIndex, cell.rowIndex, cell.columnIndex) === region.sourceId
						) {
							updateTableCell(labelIndex, cell.rowIndex, cell.columnIndex, { boundingRegions });
						}
					});
				} else if (getLabelSourceId(labelIndex) === region.sourceId) {
					updateLabel(labelIndex, { boundingRegions });
				}
			});
		},
		[fileDetails, updateTableCell, updateLabel],
	);

	return {
		fileDetails,
		fileUrl,
		isLoading: isDetailsLoading || isUrlLoading,
		fileViewerRef,
		pendingRegion,
		setPendingRegion,
		drawMode,
		setDrawMode,
		pendingCellTarget,
		pendingLabelTarget,
		regions,
		setRegions,
		selectedSourceId,
		setSelectedSourceId,
		updateLabelContent,
		handleCancelLabel,
		handleSaveLabel,
		handleCellTargetClick,
		handleLabelTargetClick,
		handleCellContentChange,
		handleCellCandidateSelect,
		handleDeleteLabel: handleDeleteEntry,
		handleDeleteTable: handleDeleteEntry,
		handleCellDelete,
		handleDeleteRow,
		handleDeleteColumn,
		handleReorderColumns,
		handleRegionCreated,
		handleRegionUpdated,
		handleAddRow,
		handleAddTable,
		handleUpdateTable,
		getColumnPinPreferences,
		handleTogglePinColumn,
	};
};
